import React from 'react';
import classNames from 'classnames';

import { Responsive, responsiveClasses } from '../helpers/responsive';
import { FgColor, fgColorClass } from '../helpers/foregroundColors';
import { SizeStep, sizeClass } from '../helpers/sizeScale';

// Relative-sizing modes (not on the fixed `size-*` scale): `fullWidth` fills the
// container, `inline` tracks the surrounding font size, `native` renders at the SVG's
// intrinsic size. These stay scalar — only the fixed numeric scale is responsive.
type IconSizeMode = 'fullWidth' | 'inline' | 'native';

// Deprecated alias for the numeric scale, kept so the ~70 existing `size="rc-space-N"`
// call-sites keep working while a follow-up codemod migrates them to `size={N}`. The
// values are identical (`rc-space-6` == `size-6` == 24px), so it maps to the same
// `.width-size-N` utility. Only steps on the `size-*` scale are accepted.
/** @deprecated pass the numeric step instead — `size={6}`, not `size="rc-space-6"`. */
type LegacyIconSize = `rc-space-${SizeStep}`;

export interface RCIconProps {
  /** A React icon component, `<svg>` element, or similar. */
  icon: JSX.Element;
  /** The rendered size of the icon.
   * - A numeric step on the `size-*` scale (e.g. `size={6}` = 24px) — the primary API.
   *   Responsive: pass a value or a `{ desktop, tablet, mobile }` object.
   * - `fullWidth` – Default. Fills 100% of the container's width.
   * - `inline` – Sizes relative to the surrounding font size.
   * - `native` – Renders at the icon's intrinsic SVG size.
   * - `rc-space-*` – Deprecated string alias of the numeric scale; use `size={N}`.
  */
  size?: Responsive<SizeStep> | IconSizeMode | LegacyIconSize;
  /** @deprecated use `size = 'inline'` instead. Using this props overrides `size` prop. */
  inline?: boolean;
  /** @deprecated use `size = 'native'` instead. Using this props overrides `size` and `inline` props. */
  nativeSize?: boolean;
  /** The icon's color, from the shared foreground palette. Icons clear the 3:1 graphical-
   *  contrast bar, so both the text tier (4.5:1) and the accent tier (3:1) are available, plus
   *  the surface-tracking `text-themed` / `accent-themed` values — inside a themed ancestor
   *  (RCThemedBox / RCCallout / `.themed-box`) those take the surface's family color, and
   *  inherit otherwise. Responsive: pass a value or a `{ desktop, tablet, mobile }` object. */
  color?: Responsive<FgColor>;
  /** Added as an `aria-label`. Leave this blank for decorative icons, but add a descriptive verb for interactive icons.
   *  Icons without a `title` wll be `aria-hidden={true}`
  */
  title?: string;
  /** Escape hatch for styles the typed props don't cover (custom sizing, layout).
   *  Appended after the component's own classes, so it wins on equal-specificity collisions. */
  className?: string;
}

/**
 * A simple wrapper for SVG elements that handles icon sizing and accessibility.
 *
 * Icons are sized to 100% of their container's width by default.
*/
export function RCIcon({
  size = 'fullWidth',
  inline = false,
  nativeSize = false,
  color = undefined,
  title = undefined,
  className = undefined,
  icon,
}: RCIconProps) {
  // The deprecated boolean props still win over `size` when set.
  const resolvedSize: RCIconProps['size'] = nativeSize ? 'native' : inline ? 'inline' : size;

  const classes = classNames(
    'rc-icon',
    {
      'rc-icon--inline': resolvedSize === 'inline',
      'rc-icon--native-size': resolvedSize === 'native',
    },
    // Fixed scale (numeric or the `rc-space-N` alias) → width utility classes; the
    // relative modes carry no width class and fall through to the base/mode CSS.
    responsiveClasses(resolvedSize, widthSizeClass),
    responsiveClasses(color, fgColorClass),
    className,
  );

  return (
    <span
      className={classes}
      aria-hidden={title ? undefined : true}
      aria-label={title || undefined}
      role={title ? 'img' : undefined}
    >
      {React.isValidElement(icon) &&
        React.cloneElement(icon, { 'aria-hidden': true } as React.HTMLAttributes<Element>)
      }
    </span>
  );
}

// Maps one `size` value at a breakpoint to its width utility class, or `false` for the
// relative modes (which have no fixed width). Accepts the deprecated `rc-space-N` alias
// by peeling the numeric step — the `size-N` value is identical.
function widthSizeClass(size: SizeStep | IconSizeMode | LegacyIconSize, prefix: string): string | false {
  if (typeof size === 'number') return sizeClass('width', size, prefix);
  if (size === 'fullWidth' || size === 'inline' || size === 'native') return false;
  const step = Number(size.replace('rc-space-', '')) as SizeStep;
  return sizeClass('width', step, prefix);
}
